import { useCallback } from "react";
import { trackSearchQuery } from "../utils/analytics";
import { sanitizeTitle, tokenizeToKeywords } from "../utils/searchHelpers";
import { errorDev } from '../utils/logger.js';

const INDEX_KEY = "orrith:search-index:v4";
const CACHE_MS = 10 * 60 * 1000;

const state = {
  fuse: null,
  items: [],
  builtAt: 0,
  building: null,
};

const CATALOG_ALIASES = {
  all: "all",
  farm: "farm",
  farmpulse: "farmpulse",
  farmpluse: "farmpulse",
  farmmark: "farmmark",
  northstar: "northstar",
  northstach: "northstar",
  platform: "platform",
  about: "platform",
};

const normalizeCatalog = (value) => {
  const key = String(value || "").toLowerCase().replace(/[^a-z]/g, "");
  return CATALOG_ALIASES[key] || "all";
};

const normalizeItem = (raw) => {
  if (!raw) return null;
  const path = String(raw.path || "/");
  const safePath = path.startsWith("/") ? path : `/${path}`;
  const title = String(raw.title || raw.displayTitle || "").trim();
  const displayTitle = String(raw.displayTitle || sanitizeTitle(title) || title || safePath).trim();
  if (!displayTitle) return null;

  const keywords = Array.isArray(raw.keywords)
    ? raw.keywords.filter(Boolean).map((k) => String(k).toLowerCase())
    : tokenizeToKeywords(`${displayTitle} ${title} ${raw.description || ""}`);

  const catalogs = Array.isArray(raw.catalogs) && raw.catalogs.length
    ? raw.catalogs.map(normalizeCatalog).filter((c) => c !== "all")
    : ["platform"];

  return {
    id: String(raw.id || `${safePath}:${displayTitle}`),
    title: title || displayTitle,
    displayTitle,
    description: String(raw.description || ""),
    path: safePath,
    type: String(raw.type || "page"),
    priority: Number.isFinite(Number(raw.priority)) ? Number(raw.priority) : 0,
    keywords,
    catalogs,
  };
};

const dedupe = (items) => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (!item) continue;
    const key = `${item.path}::${String(item.displayTitle || "").toLowerCase()}::${item.type}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

async function ensureIndex(force = false) {
  const fresh = !force && state.fuse && Date.now() - state.builtAt < CACHE_MS;
  if (fresh) return;
  if (state.building) return state.building;

  state.building = (async () => {
    const fuseMod = await import("fuse.js");
    const Fuse = fuseMod.default || fuseMod;

    let items = [];
    try {
      const raw = localStorage.getItem(INDEX_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.items) && Date.now() - Number(parsed.generatedAt || 0) < CACHE_MS) {
          items = parsed.items.map(normalizeItem).filter(Boolean);
        }
      }
    } catch (err) {
      errorDev("Operation failed:", err);
      items = [];
    }

    if (!items.length) {
      try {
        const res = await fetch("/search-index.json", { cache: "force-cache" });
        const json = res.ok ? await res.json() : { items: [] };
        const base = Array.isArray(json?.items) ? json.items : [];
        const fixedRoutes = [
           { id: "route-farm", title: "Farm", path: "/farm", type: "page", priority: 50, catalogs: ["farm"] },
          { id: "route-farmpulse", title: "FarmPulse", path: "/farmpulse", type: "page", priority: 50, catalogs: ["farmpulse"] },
          { id: "route-farmmark", title: "FarmMark", path: "/farmmark", type: "page", priority: 50, catalogs: ["farmmark"] },
          { id: "route-northstar", title: "NorthStar", path: "/north-star", type: "page", priority: 50, catalogs: ["northstar"] },
          { id: "route-platform", title: "Our Platform", path: "/orrith-landing", type: "page", priority: 45, catalogs: ["platform"] },
        ];
        items = dedupe([...base, ...fixedRoutes].map(normalizeItem).filter(Boolean));
      } catch (err) {
        errorDev("Operation failed:", err);
        items = [];
      }

      try {
        localStorage.setItem(INDEX_KEY, JSON.stringify({ generatedAt: Date.now(), items }));
      } catch (err) {
        // no-op
      errorDev("Operation failed:", err);
      }
    }

    state.items = items;
    state.fuse = new Fuse(items, {
      includeScore: true,
      shouldSort: false,
      ignoreLocation: true,
      threshold: 0.35,
      keys: [
        { name: "displayTitle", weight: 0.5 },
        { name: "keywords", weight: 0.35 },
        { name: "description", weight: 0.15 },
      ],
    });
    state.builtAt = Date.now();
  })().finally(() => {
    state.building = null;
  });

  return state.building;
}

export default function useSearchClient() {
  const prefetchIndex = useCallback(async (force = false) => ensureIndex(force), []);

  const search = useCallback(async (q, opts = {}) => {
    const query = String(q || "").trim();
    if (!query) return [];

    await ensureIndex(false);
    if (!state.fuse) return [];

    const category = normalizeCatalog(opts.category || "all");
    const limit = Math.min(100, Math.max(1, Number(opts.limit || 50)));

    const scoped = category === "all"
      ? state.items
      : state.items.filter((item) => (item.catalogs || []).includes(category));

    const scopedIds = new Set(scoped.map((item) => item.id));
    const results = state.fuse
      .search(query, { limit: limit * 5 })
      .map((hit) => ({ ...normalizeItem(hit.item), _score: Number(hit.score ?? 1) }))
      .filter((item) => item && scopedIds.has(item.id))
      .sort((a, b) => a._score - b._score)
      .slice(0, limit);

    try {
      trackSearchQuery(query, { catalog: category, resultCount: results.length, mode: "client-index-v4" });
    } catch (err) {
      // no-op
    errorDev("Operation failed:", err);
    }

    return results;
  }, []);

  return { search, prefetchIndex, _internal: state };
}
